import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, DownloadRiskMapTreeProcessViewGQL, DownloadRiskMapTreeProcessViewMutation, GetCptFiltersGQL, GetCptFiltersQuery, GetProcessRiskmapGQL, GetProcessRiskmapQuery, OperatorInput, OrderTypeInput, PagingInput, RiskMapTreeColumns, RiskMapTreeQueryComplexInput, RiskMapTreeSimpleQueryInput, RiskMapTreeSortInput, UpdateAuRiskMapProcessViewGQL, UpdateAuRiskMapProcessViewMutation, UpdateProcessRiskmapRiskGQL, UpdateProcessRiskmapRiskMutation, UpdateProcessRiskmapStatusGQL, UpdateProcessRiskmapStatusMutation } from "../../../graphql/generated";
import { map, Observable } from "rxjs";
import { MutationData, unwrapMutationData } from "../shared/graphql-mutation-response.util";

@Injectable({
    providedIn: 'root'
})

export class ProcessRiskmapService {
    private readonly GetProcessRiskmapGQL: GetProcessRiskmapGQL = inject(GetProcessRiskmapGQL);
    private readonly UpdateProcessRiskmapStatusGQL: UpdateProcessRiskmapStatusGQL = inject(UpdateProcessRiskmapStatusGQL);
    private readonly UpdateProcessRiskmapRiskGQL: UpdateProcessRiskmapRiskGQL = inject(UpdateProcessRiskmapRiskGQL);
    private readonly DownloadRiskMapTreeProcessViewGQL: DownloadRiskMapTreeProcessViewGQL = inject(DownloadRiskMapTreeProcessViewGQL);
    private readonly GetCptFiltersGQL: GetCptFiltersGQL = inject(GetCptFiltersGQL);
    private readonly UpdateAuRiskMapProcessViewGQL: UpdateAuRiskMapProcessViewGQL = inject(UpdateAuRiskMapProcessViewGQL);

    loadRiskmapProcess(campaignId: string, cptIds: string[], parameters: any, legalEntitiesIds: string[]): Observable<GetProcessRiskmapQuery['riskMapProcessView']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetProcessRiskmapGQL.fetch({ campaignId, cptIds, query: param.query, sort: param.sort, page: param.page, legalEntitiesIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapProcessView))
    }

    getCptFilters(ids: string[]): Observable<GetCptFiltersQuery['commonProcessTreeFields']> {
        return this.GetCptFiltersGQL.fetch({ ids, active: null, valueChain: null }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.commonProcessTreeFields))
    }

    updateProcessRismapStatus(cptId: string, legalEntityId: string, status: boolean, campaignId: string, legalEntitiesIds: string[]): Observable<MutationData<UpdateProcessRiskmapStatusMutation['updateRiskMapProcessViewStatus']>> {
        return this.UpdateProcessRiskmapStatusGQL.mutate({ campaignId, cptId, legalEntityId, status, legalEntitiesIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateRiskMapProcessViewStatus), unwrapMutationData('Unable to update the status'))
    }

    updateProcessRiskmapRisk(campaignId: string, legalEntityId: string, processId: string, riskId: string, value: boolean, legalEntitiesIds: string[]): Observable<MutationData<UpdateProcessRiskmapRiskMutation['updateRiskMapProcessViewRisk']>> {
        return this.UpdateProcessRiskmapRiskGQL.mutate({ campaignId, legalEntityId, processId, riskId, value, legalEntitiesIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateRiskMapProcessViewRisk), unwrapMutationData('Unable to update the risk map'))
    }

    updateAuProcessRiskmap(campaignId: string, legalEntityId: string, processId: string, legalEntitiesIds: string[]): Observable<MutationData<UpdateAuRiskMapProcessViewMutation['updateAuRiskMapProcessView']>> {
        return this.UpdateAuRiskMapProcessViewGQL.mutate({ campaignId, legalEntityId, processId, legalEntitiesIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateAuRiskMapProcessView), unwrapMutationData('Unable to update the auditable universe'))
    }

    downloadProcessRiskmap(
        campaignId: string,
        cptIds: any,
        parameters: any,
        timeZone: string,
        legalEntitiesIds: string[]
    ): Observable<DownloadRiskMapTreeProcessViewMutation['downloadRiskMapTreeProcessView']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.DownloadRiskMapTreeProcessViewGQL.mutate({ campaignId, cptIds, timeZone, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput, legalEntitiesIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadRiskMapTreeProcessView))
    }

}

export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: RiskMapTreeQueryComplexInput, sort: RiskMapTreeSortInput, page: PagingInput } {
    const query: RiskMapTreeSimpleQueryInput[] = [];

    if (parameters) {

        Object.entries(parameters?.filters).forEach(([field, values]) => {
            if (values !== null && Array.isArray(values) && values.length > 0) {
                (ENUM_PROCESS_RISKMAP[field as keyof typeof ENUM_PROCESS_RISKMAP] || [field]).forEach((key) => {
                    query.push({
                        field: key as RiskMapTreeColumns,
                        filters: {
                            operator: ComparatorOperators.Match,
                            value: values
                        }
                    });
                })
            }
        });
    }

    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query

                }
            ]
        },
        sort: {
            sortField: ENUM_PROCESS_RISKMAP?.[parameters?.order?.sortField as keyof typeof ENUM_PROCESS_RISKMAP]?.[0] as RiskMapTreeColumns || RiskMapTreeColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc || OrderTypeInput.Asc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1
        }
    }
}


export const ENUM_PROCESS_RISKMAP = {
    name: [RiskMapTreeColumns.Name],
    code: [RiskMapTreeColumns.Code],
    status: [RiskMapTreeColumns.RiskMapStatus],
    lastUpdated: [RiskMapTreeColumns.LastUpdate],
    au: [RiskMapTreeColumns.Au],
    createdAt: ['CREATED_AT']
}